import React, {
    useState,
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
} from "react";
import Loader from "../../components/ui/Loader/Loader";
import HttpAPICall from "../../services/HttpAPICall";
import { BACKOFFICE_BASE_URL } from "../../services/TapApiUrls";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DisplayListPagination } from "../../components/TapUi";
import CountUpdate from "./countUpdate";
import ModuleSubModuleUpdatePopup from "./ModuleSubModuleUpdatePopup";
import Status from "../../components/ui/Status";

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */
const IconCube = ({ size = 20, color = "#2563eb" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
            d="M12 2 3 6.5v11L12 22l9-4.5v-11L12 2Z"
            stroke={color}
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path
            d="M3 6.5 12 11l9-4.5M12 11v11"
            stroke={color}
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
    </svg>
);

const IconUsers = ({ size = 20, color = "#2563eb" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="9" cy="8" r="3.2" stroke={color} strokeWidth="1.8" />
        <path
            d="M3 19c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <circle cx="17" cy="9" r="2.4" stroke={color} strokeWidth="1.8" />
        <path
            d="M15.5 13.6c2.7 0 5 1.8 5 4.6"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);

const IconStack = ({ size = 20, color = "#2563eb" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <ellipse cx="12" cy="6" rx="8" ry="3" stroke={color} strokeWidth="1.8" />
        <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" stroke={color} strokeWidth="1.8" />
        <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" stroke={color} strokeWidth="1.8" />
    </svg>
);

const IconChart = ({ size = 20, color = "#2563eb" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect x="3.5" y="11" width="3.6" height="9" rx="0.8" fill={color} />
        <rect x="10.2" y="6.5" width="3.6" height="13.5" rx="0.8" fill={color} />
        <rect x="16.9" y="3" width="3.6" height="17" rx="0.8" fill={color} />
    </svg>
);

/* ✅ naye modules ke liye extra icons */
const IconBriefcase = ({ size = 20, color = "#2563eb" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <rect
            x="3"
            y="7.5"
            width="18"
            height="12"
            rx="2"
            stroke={color}
            strokeWidth="1.8"
        />
        <path
            d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <path d="M3 12.5h18" stroke={color} strokeWidth="1.8" />
    </svg>
);

const IconPlug = ({ size = 20, color = "#2563eb" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
            d="M9 3v5M15 3v5"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
        />
        <path
            d="M6 8h12v3a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8Z"
            stroke={color}
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path
            d="M12 17v4"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);

const IconFlask = ({ size = 20, color = "#2563eb" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path
            d="M10 3h4v5l4.5 9a2 2 0 0 1-1.8 3H7.3a2 2 0 0 1-1.8-3L10 8V3Z"
            stroke={color}
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path
            d="M7 15h10"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
        />
    </svg>
);

/* ------------------------------------------------------------------ */
/*  Module icon mapping                                                */
/* ------------------------------------------------------------------ */
const MODULE_ICONS = {
    group: (props) => <IconUsers {...props} />,
    iam: (props) => <IconCube {...props} />,
    isc: (props) => <IconStack {...props} />,
    hrm: (props) => <IconUsers {...props} />,
    scrm: (props) => <IconChart {...props} />,
    backoffice: (props) => <IconBriefcase {...props} />,
    sw_integration: (props) => <IconPlug {...props} />,
    tst: (props) => <IconFlask {...props} />,
};

const DEFAULT_MODULE_ICON = (props) => <IconCube {...props} />;

const getModuleIcon = (moduleCode) =>
    MODULE_ICONS[moduleCode] || DEFAULT_MODULE_ICON;

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */
const COUNT_FIELDS = [
    { key: "last_asset_count", label: "Assets" },
    { key: "last_sites_count", label: "Sites" },
    { key: "last_users_count", label: "Users" },
    { key: "last_items_count", label: "Items" },
    { key: "last_warehouses_count", label: "Warehouses" },
    { key: "last_deals_count", label: "Deals" },
    { key: "last_employees_count", label: "Employees" },
];

const MODULE_COUNT_ROWS = {
    group: [
        { id: "count_group_users", label: "User", field: "users", maxField: "max_users" },
        { id: "count_group_sites", label: "Site", field: "sites", maxField: "max_sites" },
    ],
    iam: [{ id: "count_iam_assets", label: "Asset", field: "assets", maxField: "max_assets" }],
    isc: [
        { id: "count_isc_warehouses", label: "Warehouse", field: "warehouses", maxField: "max_warehouses" },
        { id: "count_isc_items", label: "Item", field: "items", maxField: "max_items" },
    ],
    hrm: [{ id: "count_hrm_employees", label: "Employee", field: "employees", maxField: "max_employees" }],
    scrm: [{ id: "count_scrm_deals", label: "Deal", field: "deals", maxField: "max_deals" }],
};

const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return `${date.getDate()}-${date.toLocaleString("default", { month: "short" })}-${date.getFullYear()}`;
};

/* ------------------------------------------------------------------ */
/*  Usage bar                                                          */
/* ------------------------------------------------------------------ */
const UsageBar = ({ percent, color }) => {
    const p = Math.max(0, Math.min(100, Number(percent) || 0));
    const barColor =
        color ||
        (p >= 80 ? "#f59e0b" : p >= 60 ? "#2563eb" : "#16a34a");
    return (
        <div className="d-flex align-items-center justify-content-center">
            <div
                style={{
                    width: "80px",
                    height: "8px",
                    borderRadius: "999px",
                    backgroundColor: "#eef1f6",
                    overflow: "hidden",
                    marginRight: "10px",
                }}
            >
                <div
                    style={{
                        width: `${p}%`,
                        height: "100%",
                        backgroundColor: barColor,
                        borderRadius: "999px",
                    }}
                />
            </div>
            <span style={{ fontWeight: 600, fontSize: "12.5px", color: "#1f2a44" }}>
                {p}%
            </span>
        </div>
    );
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
const GroupDetail = forwardRef((props, ref) => {
    const access_token = useSelector((state) => state.auth.access_token);
    const [moduleData, setModuleData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [logsData, setLogsData] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [listingMeta, setListingMeta] = useState(null);
    const [activeTab, setActiveTab] = useState("details");

    const countUpdateRef = useRef(null);
    const subModuleUpdatePopupRef = useRef(null);

    useEffect(() => {
        if (props.groupDetail?.master_id) fetchModuleDetails();
    }, [props.groupDetail]);

    useEffect(() => {
        if (activeTab === "logs" && props.groupDetail?.master_id) fetchModuleLogs(1);
    }, [activeTab, props.groupDetail]);

    const fetchModuleDetails = () => {
        setLoading(true);
        HttpAPICall.withAthorization(
            "GET",
            `${BACKOFFICE_BASE_URL}/group/module_detail/${props.groupDetail.master_id}`,
            access_token,
            {},
            {},
            (resp) => {
                setModuleData(Array.isArray(resp.data?.data) ? resp.data.data : []);
            }
        ).finally(() => setLoading(false));
    };

    const fetchModuleLogs = (page = 1) => {
        if (!props.groupDetail?.master_id) return;
        setLogsLoading(true);
        HttpAPICall.withAthorization(
            "GET",
            `${BACKOFFICE_BASE_URL}/group/module_log/${props.groupDetail.master_id}`,
            access_token,
            { page, per_page: 100 },
            {},
            (resp) => {
                if (Array.isArray(resp.data?.data)) {
                    setLogsData(resp.data.data);
                    setListingMeta(resp.data);
                } else {
                    setLogsData([]);
                    setListingMeta(null);
                }
            },
            () => {
                toast.error("Failed to load logs", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                setLogsData([]);
                setListingMeta(null);
            }
        ).finally(() => setLogsLoading(false));
    };

    /* ---------- Selection ---------- */
    const isSelected = (id) => selectedIds.includes(id);

    const toggleId = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const toggleMany = (ids, shouldSelect) => {
        setSelectedIds((prev) => {
            if (shouldSelect) return Array.from(new Set([...prev, ...ids]));
            return prev.filter((id) => !ids.includes(id));
        });
    };

    /* ✅ ALL modules from API in API order */
    const orderedModules = moduleData || [];

    const allCountIds = orderedModules.flatMap(
        (m) => (MODULE_COUNT_ROWS[m.module] || []).map((r) => r.id)
    );

    const allModuleIds = orderedModules.flatMap((m) => {
        const subIds = (m.sub_modules || []).map((s) => `sub_${s.id}`);
        return [`module_${m.id}`, ...subIds];
    });

    const handleCountsHeaderCheckbox = () => {
        const allChecked = allCountIds.length > 0 && allCountIds.every(isSelected);
        toggleMany(allCountIds, !allChecked);
    };

    const handleModulesHeaderCheckbox = () => {
        const allChecked = allModuleIds.length > 0 && allModuleIds.every(isSelected);
        toggleMany(allModuleIds, !allChecked);
    };

    useImperativeHandle(ref, () => ({
        openCountUpdate: () => {
            const selected = selectedIds.filter((id) => id.startsWith("count_"));
            if (selected.length === 0) {
                toast.error("Please select at least one count to update", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }

            const payload = [];
            orderedModules.forEach((module) => {
                const rows = MODULE_COUNT_ROWS[module.module] || [];
                rows.forEach((row) => {
                    if (selected.includes(row.id)) {
                        payload.push({
                            id: row.id,
                            label: row.label,
                            moduleLabel: module.name || module.module,
                            current: row.field,               
                            max: row.maxField,                
                            currentValue: module[row.field] ?? "",             
                            maxValue: module[row.field] ?? "",                  
                            module_id: module.id,
                            tap_master_id: props.groupDetail?.master_id,
                        });
                    }
                });
            });

            countUpdateRef.current?.openEditModal(payload);
        },

        openModuleUpdate: () => {
            const selectedModuleIds = selectedIds
                .filter((id) => id.startsWith("module_"))
                .map((id) => Number(id.replace("module_", "")));
            const selectedSubIds = selectedIds
                .filter((id) => id.startsWith("sub_"))
                .map((id) => Number(id.replace("sub_", "")));

            if (selectedModuleIds.length === 0 && selectedSubIds.length === 0) {
                toast.error("Please select at least one module or sub-module", {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }

            const payload = [];

            orderedModules.forEach((m) => {
                (m.sub_modules || []).forEach((sub) => {
                    if (selectedSubIds.includes(sub.id)) {
                        payload.push({
                            id: sub.id,
                            name: sub.name,
                            type: "sub_module",
                            from: sub.from,
                            till: sub.till,
                            module_id: m.id,
                            module_from: m.from,
                            tap_master_id: props.groupDetail?.master_id,
                        });
                    }
                });
            });

            orderedModules.forEach((m) => {
                if (selectedModuleIds.includes(m.id)) {
                    payload.push({
                        id: m.id,
                        name: m.name || m.module,
                        type: "module",
                        from: m.from,
                        till: m.till,
                        module_id: m.id,
                        tap_master_id: props.groupDetail?.master_id,
                    });
                }
            });

            if (payload.length) {
                subModuleUpdatePopupRef.current?.openEditModal(payload);
            }
        },
    }));

    const handleSubModuleUpdateSuccess = () => {
        fetchModuleDetails();
        setSelectedIds([]);
    };

    const handleCountUpdateSuccess = () => {
        fetchModuleDetails();
        setSelectedIds([]);
    };

    /* ------------------------------------------------------------------ */
    /*  Shared styles                                                      */
    /* ------------------------------------------------------------------ */
    const cardStyle = {
        backgroundColor: "#fff",
        padding: "15px 10px 6px 10px",
        marginBottom: "20px",
    };

    const iconBoxStyle = {
        width: "42px",
        height: "42px",
        borderRadius: "10px",
        backgroundColor: "#eaf1ff",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: "12px",
        flexShrink: 0,
    };

    const rowCellStyle = {
        paddingTop: "10px",
        paddingBottom: "10px",
        verticalAlign: "middle",
        fontSize: "13.5px",
        color: "#1f2a44",
    };

    /* ---------- Modules & Sub-Modules table ---------- */
    const renderModulesTable = () => {
        const allChecked = allModuleIds.length > 0 && allModuleIds.every(isSelected);

        return (
            <div style={cardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <span style={iconBoxStyle}>
                            <IconCube size={22} />
                        </span>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: "16px", color: "#1f2a44" }}>
                                Modules &amp; Sub-Modules
                            </div>
                            <div style={{ color: "#6b7280", fontSize: "12.5px" }}>
                                Manage modules and sub-modules for this group
                            </div>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table align-middle mb-0 table-bordered">
                        <thead>
                            <tr className="table-secondary">
                                <th className="text-center" style={{ width: "6%" }}>
                                    <input
                                        type="checkbox"
                                        checked={allChecked}
                                        onChange={handleModulesHeaderCheckbox}
                                    />
                                </th>
                                <th style={{ width: "44%" }}>Module / Sub-Module</th>
                                <th className="text-center" style={{ width: "25%" }}>
                                    Till Date
                                </th>
                                <th className="text-center" style={{ width: "25%" }}>
                                    Status
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderedModules.map((module) => {
                                const subModules = module.sub_modules || [];
                                const moduleRowId = `module_${module.id}`;
                                const ModuleIcon = getModuleIcon(module.module);

                                return (
                                    <React.Fragment key={module.id}>
                                        <tr className="table-light">
                                            <td colSpan={4}>
                                                <div className="d-flex align-items gap-2 py-1 ps-2 ">
                                                    <span>
                                                        <ModuleIcon size={18} />
                                                    </span>
                                                    {module.name || module.module} Module
                                                </div>
                                            </td>
                                        </tr>
                                        <tr className="table-light">
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected(moduleRowId)}
                                                    onChange={() => toggleId(moduleRowId)}
                                                />
                                            </td>
                                            <td
                                                style={{
                                                    ...rowCellStyle,
                                                    paddingLeft: "20px",
                                                }}
                                            >
                                                {module.name || module.module}
                                            </td>
                                            <td className="text-center">
                                                {formatDate(module.till)}
                                            </td>
                                            <td className="text-center">
                                                {module?.status ? (
                                                    <Status
                                                        color={
                                                            module.status?.status_color_code
                                                        }
                                                    >
                                                        {module.status?.status_text}
                                                    </Status>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                        </tr>

                                        {subModules.map((sub) => {
                                            const subId = `sub_${sub.id}`;
                                            return (
                                                <tr
                                                    key={sub.id}
                                                    style={{
                                                        borderBottom: "1px solid #f0f3f8",
                                                    }}
                                                >
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected(subId)}
                                                            onChange={() => toggleId(subId)}
                                                        />
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...rowCellStyle,
                                                            paddingLeft: "20px",
                                                        }}
                                                    >
                                                        {sub.name}
                                                    </td>
                                                    <td className="text-center">
                                                        {formatDate(sub.till)}
                                                    </td>
                                                    <td className="text-center">
                                                        {sub?.status ? (
                                                            <Status
                                                                color={
                                                                    sub.status
                                                                        ?.status_color_code
                                                                }
                                                            >
                                                                {sub.status?.status_text}
                                                            </Status>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    /* ---------- Counts & Limits table ---------- */
    const renderCountsTable = () => {
        const allChecked = allCountIds.length > 0 && allCountIds.every(isSelected);

        return (
            <div style={cardStyle}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <span style={iconBoxStyle}>
                            <IconChart size={22} />
                        </span>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: "16px", color: "#1f2a44" }}>
                                Counts &amp; Limits
                            </div>
                            <div style={{ color: "#6b7280", fontSize: "12.5px" }}>
                                Configure usage counts and maximum limits for this group
                            </div>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table align-middle mb-0 table-bordered">
                        <thead>
                            <tr className="table-secondary">
                                <th className="text-center" style={{ width: "6%" }}>
                                    <input
                                        type="checkbox"
                                        checked={allChecked}
                                        onChange={handleCountsHeaderCheckbox}
                                    />
                                </th>
                                <th style={{ width: "26%" }}>Module / Count</th>
                                <th className="text-center" style={{ width: "18%" }}>
                                    Max Limit
                                </th>
                                <th className="text-center" style={{ width: "18%" }}>
                                    Current Usage
                                </th>
                                <th className="text-center" style={{ width: "32%" }}>
                                    Usage
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderedModules.map((module) => {
                                const rows = MODULE_COUNT_ROWS[module.module] || [];
                                if (rows.length === 0) return null;
                                const rowIds = rows.map((r) => r.id);
                                const allRowChecked =
                                    rowIds.length > 0 && rowIds.every(isSelected);
                                const ModuleIcon = getModuleIcon(module.module);

                                return (
                                    <React.Fragment key={module.id}>
                                        <tr className="table-light">
                                            <td
                                                colSpan={5}
                                            >
                                                <div className="d-flex align-items-center gap-2 py-1 ps-2">
                                                    <span>
                                                        <ModuleIcon size={18} />
                                                    </span>
                                                    {module.name || module.module}
                                                </div>
                                            </td>
                                        </tr>

                                        {rows.map((row) => {
                                            const current =
                                                Number(module[row.field]) || 0;
                                            const max = module[row.maxField];
                                            const percent = Math.min(
                                                100,
                                                Math.max(0, Math.round(max))
                                            );
                                            return (
                                                <tr
                                                    key={row.id}
                                                    style={{
                                                        borderBottom:
                                                            "1px solid #f0f3f8",
                                                    }}
                                                >
                                                    <td className="text-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected(row.id)}
                                                            onChange={() =>
                                                                toggleId(row.id)
                                                            }
                                                        />
                                                    </td>
                                                    <td
                                                        style={{
                                                            ...rowCellStyle,
                                                            paddingLeft: "20px",
                                                        }}
                                                    >
                                                        {row.label}
                                                    </td>
                                                    <td
                                                        className="text-center"
                                                        style={{
                                                            ...rowCellStyle,
                                                            fontWeight: 700,
                                                        }}
                                                    >
                                                        {current}
                                                    </td>
                                                    <td className="text-center">
                                                        {max !== undefined &&
                                                        max !== null
                                                            ? max
                                                            : "-"}
                                                    </td>
                                                    <td>
                                                        <UsageBar percent={percent} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    /* ---------- Logs ---------- */
    const renderCountChanges = (log) => {
        const rows = COUNT_FIELDS.filter(
            (f) => log[f.key] !== null && log[f.key] !== undefined
        ).map((f) => (
            <div key={f.key} className="d-flex justify-content-between small">
                <span className="text-muted">{f.label}:</span>
                <strong className="ms-2">{log[f.key]}</strong>
            </div>
        ));
        return rows.length ? <div className="mt-1">{rows}</div> : null;
    };

    const renderLogsTab = () => {
        if (logsLoading) return <Loader />;

        return (
            <>
                <div className="table-responsive mt-3">
                    <table className="table table-hover table-bordered bg-white my-2 align-middle">
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: "4%" }}>#</th>
                                <th style={{ width: "15%" }}>Module</th>
                                <th style={{ width: "15%" }}>Sub-Module</th>
                                <th style={{ width: "25%" }}>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logsData.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        No logs available
                                    </td>
                                </tr>
                            ) : (
                                logsData.map((log, index) => (
                                    <tr key={log.id ?? index}>
                                        <td>
                                            {listingMeta
                                                ? (listingMeta.current_page - 1) *
                                                      listingMeta.per_page +
                                                  index +
                                                  1
                                                : index + 1}
                                        </td>
                                        <td>{log.module?.name?.trim() || "-"}</td>
                                        <td>{log.sub_module?.name || "-"}</td>
                                       
                                        <td
                                            style={{
                                                whiteSpace: "pre-wrap",
                                                fontSize: "13px",
                                            }}
                                        >
                                            {log.notes || "-"}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {listingMeta && listingMeta.last_page > 1 && (
                    <DisplayListPagination
                        meta={listingMeta}
                        onPageChange={(e) => fetchModuleLogs(e.selected + 1)}
                    />
                )}
            </>
        );
    };

    return (
        <>
            <CountUpdate
                ref={countUpdateRef}
                onUpdateSuccess={handleCountUpdateSuccess}
            />
            <ModuleSubModuleUpdatePopup
                ref={subModuleUpdatePopupRef}
                onUpdateSuccess={handleSubModuleUpdateSuccess}
            />

            {props.detail_loading || loading ? (
                <Loader />
            ) : (
                <section className="item_view_container bg-white border-0">
                    <div className="tab_content_header pt-0">
                        <ul className="nav nav-tabs mb-3" role="tablist">
                            <li className="nav-item" role="presentation">
                                <button
                                    type="button"
                                    className={`nav-link ${
                                        activeTab === "details" ? "active" : ""
                                    }`}
                                    onClick={() => setActiveTab("details")}
                                >
                                    Module Details
                                </button>
                            </li>
                            <li className="nav-item" role="presentation">
                                <button
                                    type="button"
                                    className={`nav-link ${
                                        activeTab === "logs" ? "active" : ""
                                    }`}
                                    onClick={() => setActiveTab("logs")}
                                >
                                    Logs
                                </button>
                            </li>
                        </ul>

                        {activeTab === "details" && (
                            <>
                                {renderModulesTable()}
                                {renderCountsTable()}
                            </>
                        )}

                        {activeTab === "logs" && renderLogsTab()}
                    </div>
                </section>
            )}
        </>
    );
});

export default GroupDetail;